<template>
	<div id="calendar-view" :style="calendarViewStyle">
		<div class="container" :class="{'full-calendar-container': isFullCalendarView}">

			<AppointmentTypePicker 
				v-if="calendarData.appointments.length > 1 && isMobileView && !loadingSlotsFlag && !isFullCalendarView" 
				class="items-center mb-8"
				:showDropdown="showDropdown"
			>
			</AppointmentTypePicker>

			<!-- Full Calendar view -->
			<div v-if="isFullCalendarView" data-cy="full-calendar-container">
				<div id="full-calendar-toolbar">
					<!-- Selected appointment and link to change (service selection) -->
					<span v-if="isServiceSelectionEnabled" class="text-xs sm:text-base">
						{{selectedAppointment.label}}
						<a @click.prevent="setActiveView('service')" data-cy="calendar-view-back-button" class="ml-1" href="javascript:;">
							({{ getLocaleText("calendarView.changeService", "change") }})
						</a>
					</span>
					
					<!-- Dropdown to change appointment (no service selection) -->
					<v-select 
						v-else
						data-cy="appointment-dropdown"
						:clearable="false"
						@input="setAppointment"
						:value="selectedAppointment"
						:options="appointmentOptions"
						class="appointment-dropdown w-4/6 sm:w-1/3"
					></v-select>
					<a data-cy="visitor-timezone" @click.stop="openTimezoneModal" href="javascript:;">{{selectedTimezone}}</a>
				</div>
				<FullCalendarView></FullCalendarView>
			</div>

			<!-- TimeSlot Selection Simple/Default view -->
			<div v-else class="flex flex-col sm:flex-row">
				<!-- left column -->
				<div data-cy="date-picker" class="calendar-wrapper flex flex-col" :style="brandingColor">
					<date-picker></date-picker>
				</div>

				<!-- right column -->
				<!-- Loading indicator -->
				<div v-if="loadingSlotsFlag" class="timelist-wrapper flex flex-col justify-center items-center text-center">
					<svg class="animate-spin-anticlock h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
					</svg>
					<h2 class="text-sm mt-8 text-gray-400">Loading...</h2>	
				</div>

				<!-- Appoinments selector and Time slots list -->
				<div v-else class="timelist-wrapper flex flex-col" :class="{rtl: isLanguageRTL}">
					<!-- Selected appointment and link to change (service selection) -->
					<span data-cy="service-selection-choice" v-if="isServiceSelectionEnabled" class="font-xs sm:text-base service-selection-choice">
						{{selectedAppointment.label}}
						<a @click.prevent="setActiveView('service')" data-cy="calendar-view-back-button" class="ml-1" href="javascript:;">
							({{ getLocaleText("calendarView.changeService", "change") }})
						</a>
					</span>

					<AppointmentTypePicker 
						v-else-if="calendarData.appointments.length > 1 && !isMobileView" 
						class="items-center mb-2"
						:showDropdown="showDropdown"
					>
					</AppointmentTypePicker>

					<!-- User timezone selecter -->
					<div v-if="slots.length > 0">
						<p 
							:class="{'mt-4': calendarData.appointments.length > 1}" 
							class="v-label" 
							data-cy="locale-text-label-for-timezone"
						>
							{{getLocaleText("timeZoneTitle", "What time works best for you?")}}
						</p>
						<a data-cy="visitor-timezone" @click.stop="openTimezoneModal" href="javascript:;">{{selectedTimezone.replace("_", " ")}}</a>
					</div>

					<!-- Timlist selector component -->
					<timelist-selector></timelist-selector>
				</div>
			</div>

		</div>

		<!-- Timzone selector modal -->
		<modal data-cy="visitor-timezone-modal" ref="selectTimezoneModal" @close-modal="closeModal" :custom-style="{height: '420px'}" :custom-class="'timezone-selector-modal'">
			<template slot="title">
				Select Timezone
			</template>
			
			<template slot="body">
				<TimezoneSelector ref="timezoneSelector" @close-modal="closeModal" :modal-view="true" v-model="selectedTimezone"></TimezoneSelector>
			</template>
		</modal>
	</div>
</template>

<script>
import DatePicker from "./DatePicker"
import { mapGetters, mapMutations } from "vuex"
import TimezoneSelector from "../shared/TimezoneSelector"
import Modal from "../shared/Modal"
import TimelistSelector from "./TimelistSelector"
import FullCalendarView from "./FullCalendarView"
import calendarViewSpecific from "../../mixins/calendarViewSpecific.js"
import {localization} from "../../mixins/localization.js"
import AppointmentTypePicker from "./AppointmentTypePicker"

export default {
	components: { DatePicker, TimezoneSelector, TimelistSelector, FullCalendarView, Modal, AppointmentTypePicker },

	mixins: [calendarViewSpecific, localization],

	name: "CalendarView",

	mounted() {
		if (this.isMobileView) { document.getElementById("sprintful-header").scrollIntoView({ block: 'start',  behavior: 'smooth' }) } //scrolling to top in mobile view
	},

	computed: {
		...mapGetters([
			'calendarData',
			'brandingData',
			'date',
			'isSlotAvailable',
			'timezone',
			'selectedAppointment',
			'schedulingData',
			'availabilitySlots',
			'loadingSlotsFlag'
		]),

		selectedTimezone: {
			get() {
				return this.timezone;
			},

			set(value) {
				let timezone = value ? value.name : ""
				this.setTimezone(timezone);
			}
		},

		brandingColor() {
			let bgColor = this.getCalendarBg(1)
			let style = `${bgColor} color: ${this.brandingData.secondaryColor}`;
			return style
		},

		showDropdown() {
			return _.some(this.calendarData.appointments, (ap) => ap.name !== "");
		},

		isServiceSelectionEnabled() {
			return this.calendarData.settings.serviceSelectionEnabled;
		},

		isMobileView() {
			return (window.innerWidth <= 480)
		},

		calendarViewStyle() {

			if (
				window.location.search.includes("hide-logo=true") && 
				window.location.search.includes("hide-message=true") && 
				window.location.search.includes("embeded=popup")
			) {
				document.getElementsByClassName("vue-container")[0].style.paddingBottom = "0"
				return ""
			}

			return "margin-top: 60px;"
		}
	},

	methods: {
		...mapMutations([
			'setTimezone',
			'setSelectedAppointment',
			'setIsSlotAvailable',
			'setIsBookingAvailable',
			'setActiveView'
		]),

		closeModal() {
			this.$refs.selectTimezoneModal.hide();
		},

		openTimezoneModal() {
			this.$refs.selectTimezoneModal.show();
			this.$refs.timezoneSelector.openSelect();
		}
	}
}
</script>


<style scoped>
	.service-selection-choice {
		border-bottom: dotted 1px #CCC;
		padding-bottom: 1rem;
	}

</style>

<style>
	.appointment-dropdown .vs__selected {
		white-space: nowrap;
		max-width: 300px;
		overflow: hidden;
		text-overflow: ellipsis;
		display: inline-block;
	}

	#full-calendar-toolbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	#full-calendar-toolbar .appointment-dropdown .vs__selected {
		max-width: 250px;
	}

	@media(max-width: 375px) {
		.appointment-dropdown .vs__selected {
			max-width: 19rem !important;
		}

		#full-calendar-toolbar .appointment-dropdown .vs__selected {
			max-width: 19rem !important;
		}
	}

	@media(max-width: 480px) {
		.appointment-dropdown .vs__selected {
			max-width: 22rem;
		}

		#full-calendar-toolbar {
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
		}

		#full-calendar-toolbar .appointment-dropdown {
			width: 100%;
		}

		#full-calendar-toolbar .appointment-dropdown .vs__selected {
			max-width: 22rem;
		}
	}
</style>